import { useState } from "react";
import { Button } from "../../../components/ui/Button";
import { Empty } from "../../../components/Shell";
import { useT } from "../../../i18n";
import type { TeacherApi } from "../api/teacherApi";
import type { TeacherWorkItem } from "../types";
import { WorkEditor } from "./WorkEditor";

/**
 * FS-11-07 작품명·원리 태그 수정 — SCR v0.6 sfs-11-07 · SR-MDF-05 · SR-MDF-04 · SR-SEC-01.
 *  - 표: 작품(별명 · 작품명 ✎ · '교사 수정' 칩) · 자동 추론(컴파일러 autoTags) · 교사 태그(우선) · 수정
 *  - 편집 칸(WorkEditor): 작품명(≤20자) · 자동 이름으로 되돌리기 · 태그 팔레트 · 자동 태그로 되돌리기 · 저장
 *  - 상태: 자동 · 교사 수정. 자동 태그는 보존(teacher_tags 만 기록) — 스냅샷 본문·해시 불변
 * 목록은 서버 순서(별명 순) — 정렬 컨트롤 없음.
 */
export function WorksTab({
  api,
  works,
  onSaved,
  initial,
}: {
  api: TeacherApi;
  works: TeacherWorkItem[] | null;
  onSaved: (w: TeacherWorkItem) => void;
  initial?: string | null;
}) {
  const t = useT();
  const [editing, setEditing] = useState<string | null>(initial ?? null);
  if (works === null) return <p className="pf-muted">{t("common.loading")}</p>;
  const current = works.find((w) => w.snapshotId === editing) ?? null;

  return (
    <section className="pf-pane" aria-labelledby="works-h" data-testid="works-tab">
      <h2 id="works-h" className="pf-h3">
        {t("teacher.works.title")}
      </h2>
      {works.length === 0 ? (
        <Empty label={t("teacher.playground.noWorks")} />
      ) : (
        <div className="pf-tbl-scroll" tabIndex={0} role="region" aria-labelledby="works-h">
          <table className="pf-tbl" data-testid="works-table">
            <thead>
              <tr>
                <th scope="col">{t("teacher.works.colWork")}</th>
                <th scope="col">{t("teacher.works.colAuto")}</th>
                <th scope="col">{t("teacher.works.colTeacher")}</th>
                <th scope="col">
                  <span className="pf-sr-only">{t("teacher.nick.colAction")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {works.map((w) => (
                <tr
                  key={w.snapshotId}
                  data-work={w.snapshotId}
                  className={w.snapshotId === editing ? "pf-tbl__row--on" : undefined}
                >
                  <th scope="row">
                    <b>{w.nickname}</b>
                    <br />
                    {w.titleOverride !== null && (
                      <span
                        className="pf-chip-static pf-chip-static--run"
                        data-testid="title-teacher"
                      >
                        {t("teacher.works.teacherEdited")}
                      </span>
                    )}{" "}
                    <span className="pf-muted" data-testid="work-title">
                      {w.title}
                    </span>
                  </th>
                  <td>
                    <ul className="pf-chips" aria-label={t("teacher.works.colAuto")}>
                      {w.autoTags.length === 0 ? (
                        <li className="pf-muted">—</li>
                      ) : (
                        w.autoTags.map((k) => (
                          <li key={k} className="pf-chip-static pf-chip-static--info">
                            {k}
                          </li>
                        ))
                      )}
                    </ul>
                  </td>
                  <td>
                    <ul
                      className="pf-chips"
                      aria-label={t("teacher.works.colTeacher")}
                      data-testid="teacher-tags"
                      data-source={w.teacherTags === null ? "auto" : "teacher"}
                    >
                      {w.teacherTags === null ? (
                        <li className="pf-chip-static">{t("teacher.works.usingAuto")}</li>
                      ) : w.teacherTags.length === 0 ? (
                        <li className="pf-chip-static pf-chip-static--run">
                          {t("teacher.works.noTags")}
                        </li>
                      ) : (
                        w.teacherTags.map((k) => (
                          <li
                            key={k}
                            className={`pf-chip-static ${w.autoTags.includes(k) ? "pf-chip-static--ok" : "pf-chip-static--run"}`}
                          >
                            {k}
                            {!w.autoTags.includes(k) && " ✎"}
                          </li>
                        ))
                      )}
                    </ul>
                  </td>
                  <td className="pf-tbl__act">
                    <Button
                      variant="ghost"
                      className="pf-btn--sm"
                      aria-pressed={w.snapshotId === editing}
                      aria-label={t("teacher.works.editLabel", { name: w.nickname })}
                      data-testid="work-edit"
                      onClick={() => setEditing(w.snapshotId === editing ? null : w.snapshotId)}
                    >
                      {t("teacher.works.edit")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {current && (
        <WorkEditor
          key={current.snapshotId}
          work={current}
          api={api}
          onSaved={onSaved}
          onClose={() => setEditing(null)}
        />
      )}
      <p className="pf-note">{t("teacher.works.footer")}</p>
    </section>
  );
}
